import { localKeys } from './keyboard';
import { savePrefs, useGame } from './store';
import { forTouch } from './touch-words';
import { en, type MessageKey, tr } from './words';

/*
 * The game's words. Turkish (the key list) and English live here; Russian, French and German in
 * lang/ (one file per language, put together from parts), loaded only when chosen. A key a
 * language lacks is said in English. test/i18n.test.ts checks every language has every key and
 * the same {placeholders}.
 */

export type Lang = 'tr' | 'en' | 'ru' | 'fr' | 'de';

/** The languages, each by its own name, with the locale its numbers and dates are written in. */
export const LANGS: readonly { id: Lang; name: string; locale: string }[] = [
  { id: 'tr', name: 'Türkçe', locale: 'tr-TR' },
  { id: 'en', name: 'English', locale: 'en-US' },
  { id: 'de', name: 'Deutsch', locale: 'de-DE' },
  { id: 'fr', name: 'Français', locale: 'fr-FR' },
  { id: 'ru', name: 'Русский', locale: 'ru-RU' },
];

/** The locale for a language's numbers and dates. */
export function localeOf(lang: Lang): string {
  return LANGS.find((l) => l.id === lang)?.locale ?? 'en-US';
}

export type { MessageKey };

/** A language's words: every key, or (while being written) some of them. */
export type Dictionary = Partial<Record<MessageKey, string>>;

const dictionaries: Partial<Record<Lang, Dictionary>> = { tr, en };

const LOADERS: Partial<Record<Lang, () => Promise<Dictionary>>> = {
  ru: () => import('./lang/ru').then((m) => m.ru),
  fr: () => import('./lang/fr').then((m) => m.fr),
  de: () => import('./lang/de').then((m) => m.de),
};

/** Fetches a language's words (Russian, French, German come in a file of their own). */
export async function loadLanguage(lang: Lang): Promise<void> {
  if (dictionaries[lang]) return;
  const load = LOADERS[lang];
  if (!load) return;
  try {
    dictionaries[lang] = await load();
  } catch {
    // Offline before it ever loaded: English stands in.
  }
}

/** Switches the game's language (once its words are in) and remembers it. */
export async function setLanguage(lang: Lang): Promise<void> {
  await loadLanguage(lang);
  useGame.setState({ lang });
  savePrefs({ ...useGame.getState(), lang });
}

/**
 * Translates a key. A string parameter that is itself a message key is translated too, so
 * events can carry e.g. `building: 'building.terminal'`.
 */
export function translate(
  lang: Lang,
  key: MessageKey,
  params: Record<string, string | number> = {},
): string {
  const dict = dictionaries[lang] ?? en;
  const word = (k: MessageKey) => dict[k] ?? en[k];
  const template = localKeys(word(key) ?? key);
  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = params[name];
    if (value === undefined) return `{${name}}`;
    return typeof value === 'string' && value in en ? word(value as MessageKey) : String(value);
  });
}

/** React hook: a `t()` bound to the current language. */
/**
 * The translator for a component. Played with a finger, every text is said for one (no key
 * letters, "tap" for "click": touch-words.ts).
 */
export function useT() {
  const lang = useGame((s) => s.lang);
  const touch = useGame((s) => s.touch);
  return (key: MessageKey, params?: Record<string, string | number>) =>
    touch ? forTouch(translate(lang, key, params), lang) : translate(lang, key, params);
}

export function formatMoney(cents: number, lang: Lang): string {
  const fmt = new Intl.NumberFormat(localeOf(lang), {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
  return fmt.format(Math.round(cents / 100));
}
